import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { branchesApi, clientsApi, subBranchesApi } from "@/features/clients/api/clients";
import apiClient from "@/api/client";
import type { ClientBranchFormData, ClientFormData, ClientSubBranchFormData } from "@/features/clients/types";
import type { Project } from "@/features/enterprises/types";

export const clientKeys = {
  all: ["clients"] as const,
  list: () => [...clientKeys.all, "list"] as const,
  detail: (id: string) => [...clientKeys.all, "detail", id] as const,
  invoices: (id: string, params?: object) => [...clientKeys.all, "invoices", id, params] as const,
};

export function useClients(enabled = true) {
  return useQuery({
    queryKey: clientKeys.list(),
    queryFn: () => clientsApi.list(),
    enabled,
  });
}

export function useClient(id: string) {
  return useQuery({
    queryKey: clientKeys.detail(id),
    queryFn: () => clientsApi.get(id),
    enabled: !!id,
  });
}

export function useClientInvoices(clientId: string, params?: { payment_status?: string; overdue?: boolean }) {
  return useQuery({
    queryKey: clientKeys.invoices(clientId, params),
    queryFn: () => clientsApi.invoices(clientId, params),
    enabled: !!clientId,
  });
}

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ClientFormData) => clientsApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: clientKeys.all });
    },
  });
}

export function useUpdateClient(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<ClientFormData>) => clientsApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: clientKeys.all });
    },
  });
}

export function useDeleteClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => clientsApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: clientKeys.all });
    },
  });
}

// ── Branches ──────────────────────────────────────────────────────────────

export function useClientBranch(id: string) {
  return useQuery({
    queryKey: ["client-branch", id],
    queryFn: () => branchesApi.get(id),
    enabled: !!id,
  });
}

export function useUpdateClientBranch(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<ClientBranchFormData>) => branchesApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["client-branch", id] });
      qc.invalidateQueries({ queryKey: ["client-branches"] });
      qc.invalidateQueries({ queryKey: clientKeys.all });
    },
  });
}

export function useDeleteClientBranch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => branchesApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["client-branches"] });
      qc.invalidateQueries({ queryKey: clientKeys.all });
    },
  });
}

// ── Sub-branches ──────────────────────────────────────────────────────────

export function useClientSubBranch(id: string) {
  return useQuery({
    queryKey: ["client-sub-branch", id],
    queryFn: () => subBranchesApi.get(id),
    enabled: !!id,
  });
}

export function useUpdateClientSubBranch(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<ClientSubBranchFormData>) => subBranchesApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["client-sub-branch", id] });
      qc.invalidateQueries({ queryKey: ["client-sub-branches"] });
      qc.invalidateQueries({ queryKey: clientKeys.all });
    },
  });
}

export function useClientProjects(clientId: string) {
  return useQuery({
    queryKey: ["client-projects", clientId] as const,
    queryFn: () =>
      apiClient
        .get<{ data: Project[] }>("/projects/", { params: { client: clientId } })
        .then((r) => r.data.data),
    enabled: !!clientId,
  });
}

export function useDeleteClientSubBranch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => subBranchesApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["client-sub-branches"] });
      qc.invalidateQueries({ queryKey: clientKeys.all });
    },
  });
}
